/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import {
  useItemsApiData,
  useCustomersApiData,
  useSalesGeoApiData,
  usePfeApiData,
  useForecastClusterFilter,
  useLocationsApiData,
} from "../../api/filtersApi";
import loadingSvg from "../../images/bouncing-circles.svg";
import {
  itemLabels,
  customerLabels,
  salesGeoLables,
  pfeLables,
  locationLabels,
} from "./FilterDropdownLabels";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import MultipleSelectVisualizationExp from "../inputSelectType/multiInputSelectExperimentation";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { setResultVisualizationExpFilter } from "../../store/filterSlice";

const FilterDropdownExperiment = () => {
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user);
  const experimentFilter = useSelector(
    (state: RootState) => state.resultVisualizationExpFilter
  );

  const { currentSector, filterData } = userData;
  const [acValue, setAcvalue] = useState("");

  const currentSectorData: any =
    currentSector !== undefined && currentSector !== null && currentSector;
  const [selectedSector, setSelectedSector] = useState(
    currentSectorData?.sector
  );
  const [selectedMarket, setSelectedMarket] = useState(
    currentSectorData?.market
  );
  const currentSectorFilterData: any =
    filterData !== null &&
    currentSector !== null &&
    currentSector !== undefined &&
    currentSector.sector !== undefined &&
    filterData[currentSector.sector];
  const { Item, Customer, PFE } = currentSectorFilterData;

  const itemsPayload = {
    sector: selectedSector as string,
    market: selectedMarket as string,
    mapping: Item?.mappings,
    packTypeName: experimentFilter?.ppg || [],
    packageName: experimentFilter?.count || [],
    packageSizeName: experimentFilter?.size || [],
    packageTypeName: experimentFilter?.container || [],
    portfolioGroupName: experimentFilter?.mg4PortfolioGroup || [],
    productCategoryName: experimentFilter?.category || [],
    productGroupName: experimentFilter?.productGroup || [],
    productLineName: experimentFilter?.productLine || [],
    subBrandName: experimentFilter?.subBrand || [],
    brandName: experimentFilter?.brand || [],
    flavourNames: experimentFilter?.flavourNames || [],
  };
  const itemsPayloadForPFNA = {
    ...itemsPayload,
    businessReportingMaterialUnitofMeasureCode:
      acValue === "" ? "EA" : acValue === "AC1" ? "EA" : "CS",
  };

  const itemsApi = useItemsApiData(
    authState?.accessToken?.accessToken!,
    "items",
    userData?.currentSector?.sector === "PFNA"
      ? itemsPayloadForPFNA
      : itemsPayload
  );

  const customerPayload = {
    sector: selectedSector as string,
    market: selectedMarket as string,
    mapping: Customer?.mappings,
    customerBusinessUnitName: experimentFilter?.businessUnit || [],
    customerChannelName: experimentFilter?.channels || [],
    [currentSector?.sector === "LATAM"
      ? "customerDemandGroup"
      : "ibpDemandGroup"]:
      currentSector?.sector === "LATAM"
        ? experimentFilter?.demandGroups
        : experimentFilter?.ibpDemandGroup || [],
    customerGlobalName: experimentFilter?.global || [],
    customerMarketUnitName: experimentFilter?.marketUnit || [],
    customerRegionName: experimentFilter?.customerRegion || [],
    customerSectorNumber: experimentFilter?.customerSector || [],
    customerSubChannelName: experimentFilter?.subChannels || [],
    customerTradeSegmentDescription: experimentFilter?.cg1TrageSegment || [],
    keyAccount: experimentFilter?.keyAccount || [],
    customerGroupName: experimentFilter?.customerGroup || [],
  };

  const customerPayloadForPFNA = {
    ...customerPayload,
    alternateIBPDemandGroup: experimentFilter?.alternateIBPDemandGroup,
  };

  const customersApi = useCustomersApiData(
    authState?.accessToken?.accessToken!,
    "customers",
    userData?.currentSector?.sector === "PFNA"
      ? customerPayloadForPFNA
      : customerPayload
  );

  const locationsApi = useLocationsApiData(
    authState?.accessToken?.accessToken!,
    "locations",
    {
      sector: selectedSector as string,
      market: selectedMarket as string,
      mapping: currentSectorFilterData["Location"]?.mappings,
      locationCountryName: experimentFilter?.locationCountry || [],
      locationGlobalName: experimentFilter?.globalCode || [],
      locationRegionName: experimentFilter?.regionCode || [],
      locationSectorName: experimentFilter?.locationSector || [],
      planningLocationId: experimentFilter?.planningLocationId || [],
    }
  );

  const salesGeoPayload = {
    sector: selectedSector as string,
    market: selectedMarket as string,
    mapping: currentSectorFilterData["Sales Geo"]?.mappings,
    salesCountryCode: experimentFilter?.salesCountry || [],
    salesDivisionName: experimentFilter?.division || [],
    salesOrganizationCode: experimentFilter?.businessOrg || [],
    salesRegionName: experimentFilter?.salesRegion || [],
    salesSectorName: experimentFilter?.salesSector || [],
  };

  const salesGeoPayloadForPFNA = {
    ...salesGeoPayload,
    salesZoneName: experimentFilter?.zone || [],
  };

  const salesGeoApi = useSalesGeoApiData(
    authState?.accessToken?.accessToken!,
    "salesgeo",
    userData?.currentSector?.sector === "PFNA"
      ? salesGeoPayloadForPFNA
      : salesGeoPayload
  );
  const pfeApi = usePfeApiData(authState?.accessToken?.accessToken!, "pfe", {
    sector: selectedSector as string,
    market: selectedMarket as string,
    mapping: PFE?.mappings,
  });

  const clusterApi = useForecastClusterFilter(
    authState?.accessToken?.accessToken!,
    "cluster",
    currentSector ? currentSector : {}
  );

  useEffect(() => {
    setSelectedSector(currentSectorData?.sector);
    setSelectedMarket(currentSectorData?.market);
  }, [
    currentSector,
    currentSectorData?.market,
    currentSectorData?.sector,
    filterData,
  ]);

  useEffect(() => {
    itemsApi?.refetch();
    customersApi?.refetch();
    salesGeoApi?.refetch();
    pfeApi?.refetch();
    locationsApi?.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSector, selectedMarket]);

  // Add effect to refetch when filters change
  useEffect(() => {
    customersApi?.refetch();
  }, [
    experimentFilter?.businessUnit,
    experimentFilter?.channels,
    experimentFilter?.ibpDemandGroup,
    experimentFilter?.demandGroups,
    experimentFilter?.global,
    experimentFilter?.marketUnit,
    experimentFilter?.customerRegion,
    experimentFilter?.customerSector,
    experimentFilter?.subChannels,
    experimentFilter?.cg1TrageSegment,
    experimentFilter?.keyAccount,
    experimentFilter?.customerGroup,
  ]);

  // Add effect for locations API retrigger
  useEffect(() => {
    locationsApi?.refetch();
  }, [
    experimentFilter?.locationCountry,
    experimentFilter?.globalCode,
    experimentFilter?.regionCode,
    experimentFilter?.locationSector,
    experimentFilter?.planningLocationId,
  ]);

  // Add effect for salesGeo API retrigger
  useEffect(() => {
    salesGeoApi?.refetch();
  }, [
    experimentFilter?.salesCountry,
    experimentFilter?.division,
    experimentFilter?.businessOrg,
    experimentFilter?.salesRegion,
    experimentFilter?.salesSector,
  ]);

  // Add effect for items API retrigger
  useEffect(() => {
    itemsApi?.refetch();
  }, [
    experimentFilter?.ppg,
    experimentFilter?.count,
    experimentFilter?.size,
    experimentFilter?.container,
    experimentFilter?.mg4PortfolioGroup,
    experimentFilter?.category,
    experimentFilter?.productGroup,
    experimentFilter?.productLine,
    experimentFilter?.subBrand,
    experimentFilter?.brand,
    experimentFilter?.planningItem,
    experimentFilter?.flavourNames,
    experimentFilter?.ac1ac2,
  ]);

  useEffect(() => {
    if (
      experimentFilter?.ac1ac2[0] !== "" &&
      experimentFilter?.ac1ac2[0] !== undefined
    ) {
      setAcvalue(experimentFilter?.ac1ac2[0]);
    }
  }, [experimentFilter?.ac1ac2]);

  // handle Change funciton for PFNA AC1/AC2 select
  const handleChange = (event: SelectChangeEvent) => {
    setAcvalue(event.target.value);
    dispatch(
      setResultVisualizationExpFilter({
        key: "ac1ac2",
        value: [event.target.value.toLocaleUpperCase()],
      })
    );
  };

  useEffect(() => {
    if (
      currentSector?.sector === "PFNA" &&
      pfeApi?.data !== undefined &&
      pfeApi?.data["ac1ac2"] !== undefined &&
      pfeApi?.data["ac1ac2"].length > 0
    ) {
      dispatch(
        setResultVisualizationExpFilter({ key: "ac1ac2", value: ["AC1"] })
      );
      setAcvalue("AC1");
    }
  }, [pfeApi?.data, currentSector?.sector, dispatch]);

  return (
    <div className="flex flex-row">
      <div className="basis-2/4 pr-[20px]">
        <h6 className="text-lg">Item</h6>
        {itemsApi?.isLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 pt-3">
            {Item !== undefined &&
              [...Item?.labels].sort().map((label: string, index: number) => {
                return (
                  <div key={label}>
                    <div className={`pb-2 mt-4 select-label`}>{label}</div>
                    <MultipleSelectVisualizationExp
                      itemList={
                        itemsApi?.data && itemsApi?.data[itemLabels[label]]
                      }
                      itemType={itemLabels[label]}
                      itemLabel={label}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>
      <div className="basis-2/4 pr-[20px]">
        <h6 className="text-lg">Customer</h6>
        {customersApi?.isLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 pt-3">
            {Customer !== undefined &&
              [...Customer?.labels]
                .sort()
                .map((label: string, index: number) => {
                  return (
                    <div key={label}>
                      <div className={`pb-2 mt-4 select-label`}>{label}</div>
                      <MultipleSelectVisualizationExp
                        itemList={
                          customersApi?.data &&
                          customersApi?.data[customerLabels[label]]
                        }
                        itemType={customerLabels[label]}
                        itemLabel={label}
                      />
                    </div>
                  );
                })}
          </div>
        )}
      </div>
      {currentSector &&
        (currentSector?.sector === "PBNA" ||
          currentSector?.sector === "PFNA") && (
          <div className="basis-1/4 pr-[20px]">
            <h6 className="text-lg">Sales Geo</h6>

            {salesGeoApi?.isLoading ? (
              <div className="flex justify-center">
                <img
                  src={loadingSvg}
                  alt="loading"
                  className="mt-10"
                  width={40}
                  height={40}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 pt-3">
                {currentSectorFilterData &&
                  currentSectorFilterData["Sales Geo"] !== undefined &&
                  [...currentSectorFilterData["Sales Geo"]?.labels]
                    .sort()
                    .map((label: string, index: number) => {
                      return (
                        <div key={label}>
                          <div className={`pb-2 mt-4 select-label`}>
                            {label}
                          </div>
                          <MultipleSelectVisualizationExp
                            itemList={
                              salesGeoApi?.data &&
                              salesGeoApi?.data[salesGeoLables[label]]
                            }
                            itemType={salesGeoLables[label]}
                            itemLabel={label}
                          />
                        </div>
                      );
                    })}
              </div>
            )}
          </div>
        )}
      {currentSector && currentSector?.sector === "LATAM" && (
        <div className="basis-1/4 pr-[20px]">
          <h6 className="text-lg">Location</h6>
          {locationsApi?.isLoading ? (
            <div className="flex justify-center">
              <img
                src={loadingSvg}
                alt="loading"
                className="mt-10"
                width={40}
                height={40}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 pt-3">
              {currentSectorFilterData &&
                currentSectorFilterData["Location"] !== undefined &&
                [...currentSectorFilterData["Location"]?.labels]
                  .sort()
                  .map((label: string, index: number) => {
                    return (
                      <div key={label}>
                        <div className={`pb-2 mt-4 select-label`}>{label}</div>
                        <MultipleSelectVisualizationExp
                          itemList={
                            locationsApi?.data &&
                            locationsApi?.data[locationLabels[label]]
                          }
                          itemType={locationLabels[label]}
                          itemLabel={label}
                        />
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      )}

      <div className="basis-1/4">
        <h6 className="text-lg">PFE</h6>
        {pfeApi?.isLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 pt-3">
            {PFE !== undefined &&
              [...PFE?.labels].sort().map((label: string, index: number) => {
                return (
                  <>
                    {currentSector &&
                    currentSector?.sector === "LATAM" &&
                    experimentFilter?.term.toString() === "MT" &&
                    label === "AC1/AC2" ? (
                      <></>
                    ) : (
                      <>
                        <div key={label}>
                          <div className={`pb-2 mt-4 select-label`}>
                            {label}
                          </div>
                          {currentSector?.sector === "PFNA" &&
                          userData?.filterData?.PFNA?.PFE?.singleSelect?.includes(
                            label
                          ) ? (
                            <FormControl
                              sx={{
                                "& .MuiInputBase-root": {
                                  color: "white",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#616161",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: "white",
                                },
                                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    border: "none",
                                  },
                                minWidth: 186,
                              }}
                            >
                              <Select
                                id="demo-simple-select-autowidth"
                                value={acValue}
                                onChange={handleChange}
                                style={{ minWidth: 186 }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      "& .MuiMenu-list": {
                                        padding: "0px !important",
                                      },
                                    },
                                  },
                                }}
                              >
                                {pfeApi?.data !== undefined &&
                                  pfeApi?.data[pfeLables[label]].length > 0 &&
                                  pfeApi?.data[pfeLables[label]].map(
                                    (itemOption) => {
                                      return (
                                        <MenuItem
                                          style={{
                                            minWidth: 186,
                                            borderBottom: "1px solid #616161",
                                            backgroundColor: "#262626",
                                            color: "#FFFFFF",
                                            padding: "10px 16px",
                                          }}
                                          value={itemOption}
                                        >
                                          {itemOption}
                                        </MenuItem>
                                      );
                                    }
                                  )}
                              </Select>
                            </FormControl>
                          ) : (
                            <MultipleSelectVisualizationExp
                              itemList={
                                pfeApi?.data && pfeApi?.data[pfeLables[label]]
                              }
                              itemType={pfeLables[label]}
                              itemLabel={label}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </>
                );
              })}
            {!clusterApi?.isLoading && (
              <>
                <div className="pb-2 mt-4 select-label">Cluster</div>
                <MultipleSelectVisualizationExp
                  itemList={clusterApi?.data?.clusters}
                  itemType="clusters"
                  itemLabel={"Cluster"}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdownExperiment;
